/**
 * Trim / equipment-level options per make (Utrustningsnivå).
 *
 * Every value here is a TRIM name ("M Sport", "S line", "Tekna"), not a drivetrain
 * descriptor - which is why these feed the trim field, not variant. Previously named
 * VARIANT_OPTIONS_BY_MAKE and bound to `variant`, so picking a chip overwrote the
 * lookup's drivetrain string.
 *
 * Used in Lägg upp annons and Värdera bil flows. If the make is not in this map, only
 * the free-text trim input is shown.
 */

export const TRIM_OPTIONS_BY_MAKE: Record<string, string[]> = {
  Mercedes: [
    "AMG Paket",
    "AMG Night Package",
    "AMG Performance",
    "Exclusive Line",
    "Avantgarde",
  ],
  BMW: [
    "M Sport",
    "M Performance",
    "Competition Package",
    "Luxury Line",
    "Shadow Line",
    "Innovation Package",
  ],
  Audi: [
    "S line",
    "RS Paket",
    "Black Optics Package",
    "Business Package",
  ],
  Volkswagen: [
    "R-Line",
    "R",
    "GTI",
    "GTE",
    "Style Package",
    "Business Package",
    "IQ Drive Package",
  ],
  Volvo: [
    "R-Design",
    "Inscription",
    "Momentum",
    "Polestar Engineered",
    "Ultimate",
    "Core",
  ],
  Lexus: [
    "F Sport",
    "Luxury Package",
    "Executive Package",
  ],
  Toyota: [
    "GR Sport",
    "Adventure Package",
    "Executive Package",
    "Premium Package",
  ],
  Hyundai: [
    "N Line",
    "N Performance",
    "Luxury Package",
  ],
  Nissan: [
    "Nismo",
    "Tekna",
    "Acenta",
    "N-Connecta",
  ],
  Tesla: [
    "Full Self Driving (FSD)",
    "Autopilot",
    "Premium Interior",
    "Performance Package",
  ],
};

/** Makes that have predefined variant options. */
export const MAKES_WITH_TRIM_OPTIONS = new Set(
  Object.keys(TRIM_OPTIONS_BY_MAKE)
);

/**
 * Look up trim options for a make.
 *
 * Keys use the NORMALIZED make vocabulary (what normalizeMake produces and what the DB
 * stores) - e.g. "Mercedes", not "Mercedes-Benz". The alias pass below means a caller
 * passing a raw provider spelling still resolves, so a future key added in the wrong
 * dialect cannot silently return no chips the way "Mercedes-Benz" did.
 */
export function getTrimOptionsForMake(make: string | undefined): string[] {
  if (!make || !make.trim()) return [];
  // Same aliases as normalizeMake, so raw provider spellings resolve too.
  const MAKE_ALIASES: Record<string, string> = {
    "mercedes-benz": "Mercedes",
    "mercedes benz": "Mercedes",
    mb: "Mercedes",
    vw: "Volkswagen",
    "škoda": "Skoda",
  };
  const raw = make.trim();
  const trimmed = MAKE_ALIASES[raw.toLowerCase()] ?? raw;
  // Exact match first
  if (TRIM_OPTIONS_BY_MAKE[trimmed]) {
    return TRIM_OPTIONS_BY_MAKE[trimmed];
  }
  // Case-insensitive match
  const key = Object.keys(TRIM_OPTIONS_BY_MAKE).find(
    (k) => k.toLowerCase() === trimmed.toLowerCase()
  );
  return key ? TRIM_OPTIONS_BY_MAKE[key] : [];
}

export function hasTrimOptionsForMake(make: string | undefined): boolean {
  return getTrimOptionsForMake(make).length > 0;
}
